import { useEffect, useState } from 'react';
import { View, Text, Button, StyleSheet, Pressable, Image } from '../lib/react-native-compat';
import signinimage from '../assets/signin.png'
import { jwtDecode } from 'jwt-decode';
import { useAtom } from 'jotai';
import { authStateAtom, userEmailAtom, tokenAtom, userIsGoodAtom } from '../store/company';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';

// Allowed email domains for authorization
const ALLOWED_DOMAINS = [
  'strikeusa.com',
  'strike-llc.com',
  'crossfire-llc.com',
  'pickettsystems.com',
  'mcleanscp.com',
  'americanindustrial.com',
  // Pickett staff sign in with @boltcompanies.com UPNs (e.g. Christina Bauer,
  // Alissa Reno) even though their work email is @pickettsystems.com.
  'boltcompanies.com'
];

export default function Login() {
  const { instance, accounts } = useMsal();
  const [auth, setAuth] = useAtom(authStateAtom);
  const [userEmail, setUserEmail] = useAtom(userEmailAtom);
  const [token, setToken] = useAtom(tokenAtom);
  const [userIsGood, setUserIsGood] = useAtom(userIsGoodAtom);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      if (accounts.length > 0) {
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          
          handleAuthSuccess(response.idToken, response.account?.username || '');
        } catch (error) {
          console.error('Silent token acquisition failed:', error);
        }
      }
    };
    
    checkExistingAuth();
  }, []);

  const handleAuthSuccess = (idToken: string, email: string) => {
    try {
      const decoded = jwtDecode(idToken);
      
      setAuth({ idToken, decoded } as any);
      setToken(idToken);
      setUserEmail(email);
      
      // Check if email domain is authorized (UPN casing varies by tenant)
      const emailDomain = (email.split('@')[1] || '').toLowerCase();
      const isAuthorized = ALLOWED_DOMAINS.includes(emailDomain);
      setUserIsGood(isAuthorized);
      
      if (!isAuthorized) {
        setAuthError(`Access denied. Email domain ${emailDomain} is not authorized.`);
      }
    } catch (error: any) {
      setAuthError('Failed to decode token: ' + (error.message || 'Unknown error'));
    }
  };

  const handleLogin = async () => {
    setIsLoggingIn(true);
    setAuthError(null);
    
    try {
      const response = await instance.loginPopup(loginRequest);
      
      if (response.idToken && response.account) {
        handleAuthSuccess(response.idToken, response.account.username);
      } else {
        setAuthError('Login failed: No token received');
      }
    } catch (error: any) {
      setAuthError('Login error: ' + (error.message || 'Unknown error'));
      console.error('Login failed:', error);
    } finally {
      setIsLoggingIn(false);
    }
  }


  return (
    <View
      style={{
        backgroundColor: '#192440',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        minWidth: '100%',
      }}>
      <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 40, marginTop: 20, width: 300, alignItems: 'center' }}>
        <Pressable
          style={{ marginBottom: 20, opacity: isLoggingIn ? 0.6 : 1 }}
          onPress={handleLogin}
          onPressIn={() => {}}
          onPressOut={() => {}}
          disabled={isLoggingIn}>
          <Image 
            source={signinimage}
            alt="Sign in with Microsoft"
            style={{ width: 200, height: 40 }}
            resizeMode="contain" 
          />
        </Pressable>
        
        {isLoggingIn && (
          <Text style={styles.text} numberOfLines={1} ellipsizeMode="tail">Signing in...</Text>
        )}
        
        {authError && (
          <>
            <Text style={[styles.heading]} numberOfLines={1} ellipsizeMode="tail">Auth Error:</Text>
            <Text style={[styles.text, styles.error]} numberOfLines={5} ellipsizeMode="tail">{authError}</Text>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'stretch',
    justifyContent: "flex-start",
    outerWidth: "100%",
    padding: 5
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-evenly",
  },
  heading: {
    padding: 5,
    fontSize: 24,
  },
  text: {
    padding: 5,
    fontSize: 14,
  },
  error: {
    color: 'red'
  }
});